import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import {
  DisqualificationFormPayload,
  DisqualificationFormValues
} from '@/forms/DisqualificationForm/types';
import FieldError from '@/forms/FieldError';
import useDisqualificationReasons from '@/hooks/useDisqualificationReasons';
import ProgramRepository from '@/repositories/ProgramRepository';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import Textarea from '@/ui/components/Form/Textarea';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import type { Props } from './types';

const DisqualificationForm = ({ onCancel, onSuccess, personId, programQualificationId }: Props) => {
  const { addMessage, currentUser } = useContext(AppContext);
  const queryClient = useQueryClient();
  const programRepository = ProgramRepository();
  const { data: disqualificationReasons, isPending: isLoadingDisqualificationReasons } =
    useDisqualificationReasons();

  const initialValues: DisqualificationFormValues = {
    disqualification_reasons: [],
    note: '',
    rejected_by: currentUser?.id
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register
  } = useForm({ defaultValues: initialValues });

  const reject = useMutation({
    mutationFn: ({ values }: { values: DisqualificationFormPayload }) => {
      return programRepository.rejectPerson({ params: values, programQualificationId });
    },
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Successfully disqualified person' });
      personId && (await queryClient.invalidateQueries({ queryKey: ['getPerson', personId] }));
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: DisqualificationFormValues) => {
    const payload: DisqualificationFormPayload = {
      ...values,
      note: values.note ?? ''
    };
    reject.mutate({ values: payload });
  };

  return (
    <div>
      <Heading value={3} text="Why should this person be disqualified?" />

      <form onSubmit={handleSubmit(submitForm)} data-testid="disqualification-form">
        <InputGroup>
          {isLoadingDisqualificationReasons ? (
            <Spinner label="Loading disqualification reasons" />
          ) : disqualificationReasons ? (
            <>
              <Label htmlFor="" text="Disqualification Reason">
                {Object.values(disqualificationReasons).map((disqualificationReason) => {
                  const id = `disqualification-reason-${disqualificationReason.id}`;

                  return (
                    <div key={id}>
                      <Label htmlFor={id} reverse text={disqualificationReason.name}>
                        <input
                          {...register('disqualification_reasons', {
                            required: 'At least one disqualification reason is required'
                          })}
                          className="mr-1 mb-1"
                          id={id}
                          type="checkbox"
                          value={disqualificationReason.name}
                        />
                      </Label>
                    </div>
                  );
                })}
              </Label>
            </>
          ) : null}

          <FieldError message={errors.disqualification_reasons?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="note"
            render={({ field }) => {
              return (
                <Textarea
                  id="id-note"
                  labelText="Note"
                  name={field.name}
                  onChange={field.onChange}
                  placeholder="Type any additional info for this disqualification"
                />
              );
            }}
          />

          <FieldError message={errors.note?.message} />
        </InputGroup>

        <InputGroup>
          <div className="flex">
            <Button disabled={reject.isPending} type="submit">
              {reject.isPending ? <Spinner color={Color.WHITE} label="Submitting..." /> : 'Submit'}
            </Button>

            <Button className="ml-2" color={Color.WHITE} onClick={onCancel} type="button">
              Cancel
            </Button>
          </div>
        </InputGroup>
      </form>
    </div>
  );
};
export default React.memo(DisqualificationForm);
